import { fg } from '@atlaskit/platform-feature-flags/fg';
import { utils } from '@atlaskit/util-service-support/constants';
import type { RequestServiceOptions } from '@atlaskit/util-service-support/types';

import { enrichAggResponseWithGatewayTracingHeaders } from './enrich-agg-response-with-gateway-tracing-headers';
import { fetchAggResponseWithCache } from './fetch-agg-response-with-cache';
import type { AggRequest, AggResponse } from './types';

const DEFAULT_GRAPHQL_ENDPOINT = '/gateway/api/graphql';

export async function fetchAggResponse<Variables, Data>({
	graphQuery,
	variables,
	operationName,
	aggAbsoluteUrl: url = DEFAULT_GRAPHQL_ENDPOINT,
	cache,
	headers,
}: AggRequest<Variables>): Promise<AggResponse<Data>> {
	// Populated from HTTP response via requestService (not present on JSON body).
	const capturedTracingHeaders: Record<string, string> = {};
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(headers ? { ...headers } : {}),
			},
			body: JSON.stringify({
				operationName,
				query: graphQuery,
				variables,
			}),
		},
		queryParams: {
			...(operationName && {
				q: operationName,
				// `operation` is added alongside `q` so UFO's Jira resource-timing
				// `sanitiseEndpoints` keys this fetch by its operation name (e.g.
				// KnowledgeDiscoveryIntentDetectionQuery) instead of collapsing it
				// into the anonymous `gateway_api_graphql` bucket.
				operation: operationName,
			}),
		},
		reportTracingHeaders: (tracing) => {
			Object.assign(capturedTracingHeaders, tracing);
		},
	};

	// if there's a ttl use fetch with in-mem cacheing
	if (cache && fg('kd_fe_enable_api_cache')) {
		return fetchAggResponseWithCache<Variables, Data>({
			graphQuery,
			variables,
			operationName,
			cache,
			headers,
		});
	}

	const response = await utils.requestService<AggResponse<Data>>({ url }, options);
	// Attach gateway trace / request IDs for downstream tracing and analytics.
	return enrichAggResponseWithGatewayTracingHeaders(response, capturedTracingHeaders);
}
